import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import api from '../utils/api';

interface ApiKeyRecord {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  status: 'active' | 'revoked' | 'expired';
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  created_by_name: string | null;
}

const SCOPE_OPTIONS = [
  {
    value: 'create_users',
    label: 'Manage users',
    description: 'Create, read, update, and delete users (cannot delete admins)',
  },
  {
    value: 'create_assignments',
    label: 'Manage assignments',
    description: 'Create, read, update, and delete assignments (cannot delete started or completed)',
  },
  {
    value: 'assign_assignment_sets',
    label: 'Manage assignment sets',
    description: 'Edit set name, description, and items, then assign sets, create subscriptions, and cancel subscriptions',
  },
  {
    value: 'create_trainings',
    label: 'Manage trainings',
    description: 'Create, read, update, and delete trainings, categories, sections, content blocks, and quiz questions',
  },
  {
    value: 'create_documents',
    label: 'Manage documents',
    description: 'Create, read, update, and delete documents and categories, including new versions',
  },
  {
    value: 'create_checklists',
    label: 'Manage checklists',
    description: 'Create, read, update, and delete checklists and items, including revisions',
  },
  {
    value: 'create_log_templates',
    label: 'Manage log templates',
    description: 'Create, read, update, and delete log templates and fields, including revisions',
  },
];

const SCOPE_LABELS: Record<string, string> = Object.fromEntries(
  SCOPE_OPTIONS.map((s) => [s.value, s.label])
);

const ApiKeys: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKeyRecord | null>(null);
  const [formName, setFormName] = useState('');
  const [formScopes, setFormScopes] = useState<string[]>([]);
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  const [revealOpen, setRevealOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState('');
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRecord | null>(null);
  const [revoking, setRevoking] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  const fetchKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api-keys/');
      setKeys(response.data.results || response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const resetForm = () => {
    setFormName('');
    setFormScopes([]);
    setFormExpiresAt('');
    setEditingKey(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (key: ApiKeyRecord) => {
    setEditingKey(key);
    setFormName(key.name);
    setFormScopes(key.scopes);
    setFormExpiresAt(key.expires_at ? key.expires_at.slice(0, 10) : '');
    setDialogOpen(true);
  };

  const handleScopeToggle = (scope: string) => {
    setFormScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setError('Name is required');
      return;
    }
    if (formScopes.length === 0) {
      setError('Select at least one scope');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload: Record<string, unknown> = {
        name: formName.trim(),
        scopes: formScopes,
      };
      if (formExpiresAt) {
        payload.expires_at = new Date(formExpiresAt).toISOString();
      } else if (editingKey) {
        payload.expires_at = null;
      }

      if (editingKey) {
        await api.patch(`/api-keys/${editingKey.id}/`, payload);
        setSuccess('API key updated');
        setDialogOpen(false);
        resetForm();
        await fetchKeys();
      } else {
        const response = await api.post('/api-keys/', payload);
        setDialogOpen(false);
        resetForm();
        setRevealedKey(response.data.key);
        setRevealOpen(true);
        await fetchKeys();
      }
    } catch (err: any) {
      const data = err.response?.data;
      setError(
        data?.scopes?.[0] ||
          data?.name?.[0] ||
          data?.detail ||
          'Failed to save API key'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setRevoking(true);
      await api.delete(`/api-keys/${revokeTarget.id}/`);
      setRevokeTarget(null);
      setSuccess('API key revoked');
      await fetchKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke API key');
    } finally {
      setRevoking(false);
    }
  };

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(revealedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
  };

  const statusColor = (status: string): 'success' | 'default' | 'error' => {
    if (status === 'active') return 'success';
    if (status === 'expired') return 'default';
    return 'error';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyIcon color="primary" />
            <Typography variant="h4" component="h1">
              API Keys
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Create API Key
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generate API keys for AI agents to perform scoped actions in your company. Keys are shown
          only once at creation — store them securely.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardHeader title="Usage" subheader="How AI agents authenticate and which endpoints each scope allows" />
          <CardContent>
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.8rem' }}>
{`Authorization: Api-Key cc_live_xxxxxxxx
Base URL: ${apiBaseUrl}/

# ── create_users scope ──────────────────────────────────────

GET    /users/
GET    /users/{id}/
POST   /users/
PATCH  /users/{id}/
DELETE /users/{id}/              # cannot delete admin users
POST   /users/{id}/archive/      # cannot archive admin users
POST   /users/{id}/disable/      # cannot disable admin users
GET    /departments/
GET    /locations/

POST /users/
{
  "email": "agent-created@example.com",
  "first_name": "Jane",
  "last_name": "Doe",
  "role": "employee",
  "employment_status": "active",
  "timezone": "America/Los_Angeles"
}

PATCH /users/{id}/
{
  "first_name": "Janet",
  "department_id": 1,
  "location_id": 2
}

# ── create_assignments scope ────────────────────────────────

GET    /users/
GET    /assignments/
GET    /assignments/{id}/
POST   /assignments/
PATCH  /assignments/{id}/
DELETE /assignments/{id}/        # only if not started or completed
POST   /assignments/bulk_create_by_target/
POST   /assignments/bulk_update_due_date/
GET    /trainings/
GET    /documents/
GET    /checklists/

POST /assignments/
{
  "assignment_type": "training",
  "content_type_str": "training",
  "object_id": 1,
  "assignee_ids": [1, 2],
  "due_date": "2026-12-31"
}

PATCH /assignments/{id}/
{
  "due_date": "2026-12-31T23:59:59Z",
  "assignee_ids": [1, 2, 3]
}

POST /assignments/bulk_create_by_target/
{
  "assignment_type": "training",
  "content_type_str": "training",
  "object_id": 1,
  "due_date": "2026-12-31",
  "filters": {
    "department_ids": [1],
    "location_ids": [],
    "role": ["employee"],
    "user_ids": []
  },
  "ignore_duplicates": false
}

POST /assignments/bulk_update_due_date/
{
  "assignment_ids": [1, 2],
  "due_date": "2026-12-31"
}

# assignment_type / content_type_str: document | training | checklist | log

# ── assign_assignment_sets scope ────────────────────────────

GET    /users/
GET    /departments/
GET    /locations/
GET    /assignment-sets/
GET    /assignment-sets/{id}/
PATCH  /assignment-sets/{id}/
POST   /assignment-sets/{id}/assign_now/
POST   /assignment-sets/{id}/create_subscription/
POST   /assignment-sets/{id}/cancel_subscriptions/

PATCH /assignment-sets/{id}/
{
  "name": "New hire set",
  "description": "Updated contents",
  "items": [
    {"content_type_str": "training", "object_id": 1},
    {"content_type_str": "document", "object_id": 2}
  ]
}

# Sending items replaces the set contents. Omit items to change only the name or description.
# content_type_str: document | training | checklist | log

POST /assignment-sets/{id}/assign_now/
{
  "user_ids": [1, 2],
  "department_ids": [],
  "location_ids": [],
  "due_date": "2026-12-31",
  "ignore_duplicates": false
}

POST /assignment-sets/{id}/create_subscription/
{
  "target_type": "user",
  "target_ids": [1, 2],
  "due_date": "2026-12-31",
  "future_member_due_days": 14
}

POST /assignment-sets/{id}/cancel_subscriptions/
{
  "subscription_ids": [1, 2]
}

# target_type: user | department | location

# ── create_trainings scope ──────────────────────────────────

GET    /training-categories/
POST   /training-categories/
PATCH  /training-categories/{id}/
DELETE /training-categories/{id}/
GET    /trainings/
GET    /trainings/{id}/
POST   /trainings/
PATCH  /trainings/{id}/
DELETE /trainings/{id}/
POST   /trainings/{id}/create_revision/
GET    /training-sections/
POST   /training-sections/
PATCH  /training-sections/{id}/
DELETE /training-sections/{id}/
GET    /content-blocks/
POST   /content-blocks/
PATCH  /content-blocks/{id}/
DELETE /content-blocks/{id}/
POST   /content-blocks/upload-image/
GET    /quiz-questions/
POST   /quiz-questions/
PATCH  /quiz-questions/{id}/
DELETE /quiz-questions/{id}/

POST /trainings/
{
  "title": "Heat Illness Prevention",
  "description": "Required annual training",
  "status": "active",
  "sections": [
    {
      "title": "Overview",
      "order": 1,
      "content_blocks": [
        {
          "block_type": "text",
          "title": "Introduction",
          "order": 1,
          "content": {"text": "Stay hydrated and take shade breaks."}
        }
      ]
    }
  ]
}

# Edits are blocked after someone starts the training. Create a revision instead.
POST /trainings/{id}/create_revision/

# ── create_documents scope ──────────────────────────────────

GET    /document-categories/
POST   /document-categories/
PATCH  /document-categories/{id}/
DELETE /document-categories/{id}/
GET    /documents/
GET    /documents/{id}/
GET    /documents/{id}/file/
POST   /documents/                 # multipart: title, file, optional description
PATCH  /documents/{id}/
DELETE /documents/{id}/            # deactivates the document
POST   /documents/{id}/create_version/   # multipart: file

POST /document-categories/
{
  "name": "Safety Policies",
  "description": "Company safety documents"
}

# ── create_checklists scope ─────────────────────────────────

GET    /checklists/
GET    /checklists/{id}/
POST   /checklists/
PATCH  /checklists/{id}/
DELETE /checklists/{id}/
POST   /checklists/{id}/create_revision/
GET    /checklist-items/
POST   /checklist-items/
PATCH  /checklist-items/{id}/
DELETE /checklist-items/{id}/
POST   /checklist-items/{id}/reorder/

POST /checklists/
{
  "title": "Daily safety inspection",
  "description": "Start of shift",
  "category": "safety",
  "items": [
    {"title": "Check fire extinguisher", "item_type": "checkbox", "order": 1, "is_required": true}
  ]
}

# ── create_log_templates scope ──────────────────────────────

GET    /log-templates/
GET    /log-templates/{id}/
POST   /log-templates/
PATCH  /log-templates/{id}/
DELETE /log-templates/{id}/
POST   /log-templates/{id}/create_revision/
GET    /log-template-fields/
POST   /log-template-fields/
PATCH  /log-template-fields/{id}/
DELETE /log-template-fields/{id}/

POST /log-templates/
{
  "title": "Injury incident",
  "description": "Record a workplace injury",
  "category": "injury",
  "fields": [
    {"title": "What happened?", "item_type": "text_input", "order": 1, "is_required": true}
  ]
}`}
            </Typography>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key prefix</TableCell>
                  <TableCell>Scopes</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No API keys yet. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  keys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell>{key.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {key.key_prefix}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {key.scopes.map((scope) => (
                            <Chip
                              key={scope}
                              label={SCOPE_LABELS[scope] || scope}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={key.status} color={statusColor(key.status)} size="small" />
                      </TableCell>
                      <TableCell>{formatDate(key.created_at)}</TableCell>
                      <TableCell>{formatDate(key.last_used_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(key)}
                            disabled={key.status === 'revoked'}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revoke">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRevokeTarget(key)}
                            disabled={key.status === 'revoked'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingKey ? 'Edit API Key' : 'Create API Key'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g. HR Automation Agent"
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>
            Permissions
          </Typography>
          {SCOPE_OPTIONS.map((scope) => (
            <Box key={scope.value} sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formScopes.includes(scope.value)}
                    onChange={() => handleScopeToggle(scope.value)}
                  />
                }
                label={scope.label}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                {scope.description}
              </Typography>
            </Box>
          ))}
          <TextField
            margin="dense"
            label="Expiration date (optional)"
            type="date"
            fullWidth
            value={formExpiresAt}
            onChange={(e) => setFormExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : editingKey ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revealOpen} onClose={() => setRevealOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now. You will not be able to see it again.
          </Alert>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" fontFamily="monospace" sx={{ flex: 1, wordBreak: 'break-all' }}>
              {revealedKey}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton onClick={handleCopyKey} color="primary">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevealOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Revoke &quot;{revokeTarget?.name}&quot;? This cannot be undone and the key will stop
            working immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button onClick={handleRevoke} color="error" variant="contained" disabled={revoking}>
            {revoking ? 'Revoking...' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiKeys;
